import { post } from "../../utils/Backend";
import { useH5Mock } from "../../global/GlobalMock";
import {
  mockEnterCompanionRoom,
  mockGetCompanionRoomFiles,
  mockGetCompanionRoomInfo,
  mockGetLatestCompanionMessages,
  mockGetMyMessagesWithReplies,
  mockGetOlderCompanionMessages,
  mockGetTopPinnedMessages,
  mockPullNewCompanionMessages,
  mockSendCompanionMessage,
} from "./CompanionRoomMock";
import type {
  EnterCompanionRoomParameter,
  EnterCompanionRoomResult,
  GetCompanionRoomFilesParameter,
  GetCompanionRoomFilesResult,
  GetCompanionRoomInfoParameter,
  GetCompanionRoomInfoResult,
  GetLatestCompanionMessagesParameter,
  GetLatestCompanionMessagesResult,
  GetMyMessagesWithRepliesParameter,
  GetMyMessagesWithRepliesResult,
  GetOlderCompanionMessagesParameter,
  GetOlderCompanionMessagesResult,
  GetTopPinnedCompanionMessagesParameter,
  GetTopPinnedCompanionMessagesResult,
  PullNewCompanionMessagesParameter,
  PullNewCompanionMessagesResult,
  SendCompanionMessageParameter,
  SendCompanionMessageResult,
} from "./CompanionRoomType";

const mockCompanionRoom = useH5Mock && false;

function unwrap<R>(res: R | string): R {
  if (typeof res === "string") {
    throw new Error(res);
  }
  return res;
}

const PREFIX = "/lark/app/intradayCompanion";

export const CompanionRoomApi = {
  async getRoomInfo(p: GetCompanionRoomInfoParameter): Promise<GetCompanionRoomInfoResult> {
    if (mockCompanionRoom) {
      return mockGetCompanionRoomInfo(p);
    }
    return unwrap(await post<GetCompanionRoomInfoParameter, GetCompanionRoomInfoResult>(
      `${PREFIX}/getRoomInfo`,
      p
    ));
  },

  async enterRoom(p: EnterCompanionRoomParameter): Promise<EnterCompanionRoomResult> {
    if (mockCompanionRoom) {
      return mockEnterCompanionRoom(p);
    }
    return unwrap(
      await post<EnterCompanionRoomParameter, EnterCompanionRoomResult>(
        `${PREFIX}/enterRoom`,
        p
      )
    );
  },

  async getLatestMessages(
    p: GetLatestCompanionMessagesParameter
  ): Promise<GetLatestCompanionMessagesResult> {
    if (mockCompanionRoom) {
      return mockGetLatestCompanionMessages(p);
    }
    return unwrap(
      await post<GetLatestCompanionMessagesParameter, GetLatestCompanionMessagesResult>(
        `${PREFIX}/getLatestMessages`,
        p
      )
    );
  },

  async getOlderMessages(
    p: GetOlderCompanionMessagesParameter
  ): Promise<GetOlderCompanionMessagesResult> {
    if (mockCompanionRoom) {
      return mockGetOlderCompanionMessages(p);
    }
    return unwrap(
      await post<GetOlderCompanionMessagesParameter, GetOlderCompanionMessagesResult>(
        `${PREFIX}/getOlderMessages`,
        p,
        { silent: true }
      )
    );
  },

  async pullNewMessagesAndHeartbeat(
    p: PullNewCompanionMessagesParameter
  ): Promise<PullNewCompanionMessagesResult> {
    if (mockCompanionRoom) {
      return mockPullNewCompanionMessages(p);
    }
    return unwrap(
      await post<PullNewCompanionMessagesParameter, PullNewCompanionMessagesResult>(
        `${PREFIX}/pullNewMessagesAndHeartbeat`,
        p,
        { silent: true }
      )
    );
  },

  async sendMessage(p: SendCompanionMessageParameter): Promise<SendCompanionMessageResult> {
    if (mockCompanionRoom) {
      return mockSendCompanionMessage(p);
    }
    return unwrap(
      await post<SendCompanionMessageParameter, SendCompanionMessageResult>(
        `${PREFIX}/sendMessage`,
        p
      )
    );
  },

  async getMyMessagesWithReplies(
    p: GetMyMessagesWithRepliesParameter
  ): Promise<GetMyMessagesWithRepliesResult> {
    if (mockCompanionRoom) {
      return mockGetMyMessagesWithReplies(p);
    }
    return unwrap(
      await post<GetMyMessagesWithRepliesParameter, GetMyMessagesWithRepliesResult>(
        `${PREFIX}/getMyMessagesWithReplies`,
        p
      )
    );
  },

  async getRoomFiles(p: GetCompanionRoomFilesParameter): Promise<GetCompanionRoomFilesResult> {
    if (mockCompanionRoom) {
      return mockGetCompanionRoomFiles(p);
    }
    return unwrap(
      await post<GetCompanionRoomFilesParameter, GetCompanionRoomFilesResult>(
        `${PREFIX}/getRoomFiles`,
        p
      )
    );
  },

  async getTopPinnedMessages(
    p: GetTopPinnedCompanionMessagesParameter
  ): Promise<GetTopPinnedCompanionMessagesResult> {
    if (mockCompanionRoom) {
      return mockGetTopPinnedMessages(p);
    }
    return unwrap(
      await post<
        GetTopPinnedCompanionMessagesParameter,
        GetTopPinnedCompanionMessagesResult
      >(`${PREFIX}/getTopPinnedMessages`, p)
    );
  },
};
